import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { cn } from '../../../../../lib/utils';

export interface RichTextInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  placeholder?: string;
  fullWidth?: boolean;
  transparent?: boolean;
  whiteText?: boolean;
  compact?: boolean;
  InputProps?: {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  };
  inputProps?: React.HTMLAttributes<HTMLDivElement>;
  onSlotClick?: (chip: HTMLSpanElement) => void;
}

const getCaretCharacterOffsetWithin = (element: HTMLElement): number | null => {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(element);
    preRange.setEnd(range.startContainer, range.startOffset);
    return preRange.toString().length;
  }
  return null;
};

const setCaretPosition = (el: HTMLElement, offset: number): void => {
  const result = Array.from(el.childNodes).reduce(
    (acc, node) => {
      if (acc.found) return acc;
      const txt = node.textContent || '';
      if (acc.remaining <= txt.length) {
        return { remaining: 0, node, offset: acc.remaining, found: true };
      }
      return { remaining: acc.remaining - txt.length, found: false };
    },
    { remaining: offset, found: false } as {
      remaining: number;
      found: boolean;
      node?: ChildNode;
      offset?: number;
    }
  );
  if (
    result.found &&
    result.node !== undefined &&
    result.offset !== undefined
  ) {
    const range = document.createRange();
    range.setStart(result.node, result.offset);
    range.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
};

const generateSlotHTML = (value: string): string => {
  let tokenCounter = 0;
  const parts = value.split(/(\{[^}]+\})/g);
  const newHTML = parts
    .map((part, index) => {
      if (/^\{[^}]+\}$/.test(part)) {
        const idx = tokenCounter;
        tokenCounter += 1;
        const slotName = part.slice(1, -1);
        const chipHtml =
          '<span contenteditable="false" class="slot-chip slot-chip-clean" ' +
          `data-token-index="${idx}" data-slot-name="${slotName}">${part}</span>`;
        const isLastPart = index === parts.length - 1;
        const nextPartIsEmpty =
          index < parts.length - 1 && parts[index + 1] === '';
        if (isLastPart || nextPartIsEmpty) {
          return `${chipHtml}\u200B`;
        }
        return chipHtml;
      }
      return part;
    })
    .join('');

  return DOMPurify.sanitize(newHTML, {
    ALLOWED_TAGS: ['span'],
    ALLOWED_ATTR: [
      'contenteditable',
      'class',
      'data-token-index',
      'data-slot-name'
    ],
    KEEP_CONTENT: true
  });
};

const RichTextInput = React.forwardRef<HTMLDivElement, RichTextInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onBlur,
      placeholder = '',
      fullWidth = false,
      transparent = false,
      whiteText = false,
      compact = false,
      InputProps = {},
      inputProps = {},
      onSlotClick
    },
    ref
  ) => {
    const localRef = useRef<HTMLDivElement>(null);
    const divRef = (ref as React.MutableRefObject<HTMLDivElement>) || localRef;

    useEffect(() => {
      if (!divRef.current) return;
      if (divRef.current.innerText === value) return;

      const caretOffset =
        document.activeElement === divRef.current
          ? getCaretCharacterOffsetWithin(divRef.current)
          : null;

      const sanitisedHTML = generateSlotHTML(value);
      divRef.current.innerHTML = sanitisedHTML;

      if (caretOffset !== null) {
        setCaretPosition(divRef.current, caretOffset);
      }
    }, [value, divRef]);

    const handleInput: React.FormEventHandler<HTMLDivElement> = () => {
      if (divRef.current) onChange(divRef.current.innerText);
    };

    const isSlotChip = (node: ChildNode | null): boolean => {
      return (
        node !== null &&
        node.nodeType === Node.ELEMENT_NODE &&
        (node as HTMLElement).classList.contains('slot-chip')
      );
    };

    const getNodeBeforeCursor = (
      container: Node,
      offset: number
    ): ChildNode | null => {
      if (container.nodeType === Node.TEXT_NODE) {
        const textContent = container.textContent || '';
        const isAtZeroWidthSpace = offset === 1 && textContent === '\u200B';
        const isAtStart = offset === 0;
        if (isAtZeroWidthSpace || isAtStart) {
          return container.previousSibling;
        }
        return null;
      }
      return container.childNodes[offset - 1] || null;
    };

    const getNodeAfterCursor = (
      container: Node,
      offset: number
    ): ChildNode | null => {
      if (container.nodeType === Node.TEXT_NODE) {
        const isAtEnd = offset === container.textContent?.length;
        return isAtEnd ? container.nextSibling : null;
      }
      return container.childNodes[offset] || null;
    };

    const deleteSlotChip = (chip: ChildNode): void => {
      const { nextSibling } = chip;
      chip.remove();
      if (
        nextSibling &&
        nextSibling.nodeType === Node.TEXT_NODE &&
        nextSibling.textContent === '\u200B'
      ) {
        nextSibling.remove();
      }
      if (divRef.current) {
        onChange(divRef.current.innerText);
      }
    };

    const handleBackspaceOnSlotChip = (
      startContainer: Node,
      startOffset: number
    ): boolean => {
      const nodeBeforeCursor = getNodeBeforeCursor(startContainer, startOffset);
      if (isSlotChip(nodeBeforeCursor)) {
        deleteSlotChip(nodeBeforeCursor as ChildNode);
        return true;
      }
      return false;
    };

    const handleDeleteOnSlotChip = (
      startContainer: Node,
      startOffset: number
    ): boolean => {
      const nodeAfterCursor = getNodeAfterCursor(startContainer, startOffset);
      if (isSlotChip(nodeAfterCursor)) {
        if (nodeAfterCursor) {
          nodeAfterCursor.remove();
          if (divRef.current) {
            onChange(divRef.current.innerText);
          }
        }
        return true;
      }
      return false;
    };

    const handleKeyDownInternal: React.KeyboardEventHandler<HTMLDivElement> = (
      e
    ) => {
      if (!divRef.current) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);
        const { startContainer, startOffset, endOffset } = range;

        if (startOffset !== endOffset) return;

        let handled = false;
        if (e.key === 'Backspace') {
          handled = handleBackspaceOnSlotChip(startContainer, startOffset);
        } else if (e.key === 'Delete') {
          handled = handleDeleteOnSlotChip(startContainer, startOffset);
        }

        if (handled) {
          e.preventDefault();
          return;
        }
      }

      onKeyDown?.(e);
    };

    const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'SPAN' && /^\{[^}]+\}$/.test(target.innerText)) {
        e.preventDefault();
        onSlotClick?.(target as HTMLSpanElement);
      }
    };

    const textColor = whiteText ? 'text-white' : 'text-black';
    const wrapperSizing = compact ? 'px-0' : 'min-h-[2.25rem] px-2';
    const inputClass = cn(
      'flex-1 rounded-md text-sm outline-none leading-snug',
      'whitespace-pre-wrap break-words box-border',
      transparent
        ? `bg-transparent ${textColor}`
        : `bg-white ${textColor} border border-gray-200`,
      fullWidth && 'w-full'
    );

    /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    return (
      <div
        className={cn(
          'flex items-center',
          wrapperSizing,
          fullWidth ? 'w-full' : ''
        )}
      >
        {InputProps.startAdornment}
        <div
          ref={divRef}
          tabIndex={0}
          contentEditable
          suppressContentEditableWarning
          aria-label={placeholder || 'Rich text input'}
          aria-multiline='true'
          data-placeholder={placeholder}
          onInput={handleInput}
          onKeyDown={handleKeyDownInternal}
          onBlur={onBlur}
          onMouseDown={handleMouseDown}
          className={inputClass}
          {...inputProps}
        />
        {InputProps.endAdornment}
      </div>
    );
    /* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  }
);

RichTextInput.displayName = 'RichTextInput';

export default RichTextInput;
